<template>
  <section class="section-base form-section-base">
    <div class="form-section-base__title">Сведения о заявителе</div>
    <v-container>
      <div class="form-section-base__controls">
        <v-row>
          <v-col>
            <v-radio-group v-model="role" row>
              <v-radio
                label="Физическое лицо"
                :value="Role.PERSON"
                :disabled="roleType !== Role.PERSON"
              />
              <v-radio
                label="Юридическое лицо"
                :value="Role.ORGANIZATION"
                :disabled="roleType !== Role.ORGANIZATION"
              />
              <v-radio
                label="Иностранное юридическое лицо"
                :value="Role.FOREIGN_ORGANIZATION"
                :disabled="
                  ![Role.ORGANIZATION, Role.FOREIGN_ORGANIZATION].includes(
                    roleType
                  )
                "
              />
            </v-radio-group>
          </v-col>
        </v-row>
      </div>

      <Person v-if="role === Role.PERSON" :v="$v.person" />

      <Organization v-if="role === Role.ORGANIZATION" :v="$v.organization" />

      <ForeignOrganization
        v-if="role === Role.FOREIGN_ORGANIZATION"
        :v="$v.foreignOrganization"
      />

      <Footer
        @next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
        @prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
        :first-step="isFirstStep"
        :loading-next="loadingNext"
      />
    </v-container>
  </section>
</template>

<script>
import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';

import { Role } from '@/utils/dictionaries';
import { checkInvalid, leaveStep } from '@/utils/services';
import Footer from '@/components/services/elements/Footer';
import { ServicesModule } from '@/store/modules/services';
import { AuthModule } from '@/store/modules/auth';
import { StepDirection, StepName } from '@/types/services';
import { trusteeModel } from '@/components/services/elements/TrusteeView';
import Person, { personModel, personValidation } from './Person';
import Organization, {
  organizationModel,
  getOrganizationValidation,
} from './Organization';
import ForeignOrganization, {
  foreignOrganizationModel,
  getForeignOrganizationValidation,
} from './ForeignOrganization';
import { CorrectTranslationStep } from '@/constants';

export default {
  name: 'Manual',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
    roleType: {
      type: String,
      required: true,
    },
  },
  components: {
    Person,
    Organization,
    ForeignOrganization,
    Footer,
  },
  data() {
    return {
      StepDirection,
      StepName,
      leaveStep,
      Role,
      invalidFields: [],
      role: this.roleType,
      person: cloneDeep(personModel),
      organization: cloneDeep(organizationModel),
      foreignOrganization: cloneDeep(foreignOrganizationModel),
    };
  },
  computed: {
    isFirstStep() {
      return CorrectTranslationStep.APPLICANT === StepName.S1;
    },
  },
  validations() {
    return {
      person: personValidation,
      organization: getOrganizationValidation(this.organization),
      foreignOrganization: getForeignOrganizationValidation(
        this.foreignOrganization
      ),
    };
  },
  created() {
    this.prefillDraft();
  },
  methods: {
    prefillDraft() {
      const applicantStore = cloneDeep(
        ServicesModule.statement.subjects.applicant
      );

      if (
        !applicantStore.person &&
        !applicantStore.organization &&
        !applicantStore.foreignOrganization
      ) {
        this.prefillEsia();
        return;
      }

      if (
        applicantStore.trustee?.documents &&
        !applicantStore.trustee.documents.statusDocument
      ) {
        applicantStore.trustee.documents.statusDocument = cloneDeep(
          trusteeModel.documents.statusDocument
        );
      }

      if (applicantStore.person) {
        this.role = Role.PERSON;
        this.person = deepmerge(personModel, applicantStore.person);
      }

      if (applicantStore.organization) {
        this.role = Role.ORGANIZATION;
        this.organization = deepmerge.all([
          organizationModel,
          applicantStore.organization,
          {
            trustee: applicantStore.trustee,
          },
        ]);
      }

      if (applicantStore.foreignOrganization) {
        this.role = Role.FOREIGN_ORGANIZATION;
        this.foreignOrganization = deepmerge.all([
          foreignOrganizationModel,
          applicantStore.foreignOrganization,
          {
            trustee: applicantStore.trustee,
          },
        ]);
      }
    },

    prefillEsia() {
      if (this.roleType === Role.PERSON) {
        this.prefillEsiaPerson();
      } else if (this.roleType === Role.ORGANIZATION) {
        this.prefillEsiaOrganization();
        this.prefillEsiaForeignOrganization();
      }
    },

    prefillEsiaPerson() {
      const user = cloneDeep(AuthModule.user);
      this.person = deepmerge(this.person, {
        lastName: user.lastName || null,
        firstName: user.firstName || null,
        patronymic: user.middleName || null,
        snils: user.snils || null,
        inn: user.inn || null,
        email: (user.contactInfo && user.contactInfo.email) || null,
        phone: (user.contactInfo && user.contactInfo.mobilePhone) || null,
      });
    },

    async prefillEsiaOrganization() {
      const userDetails = cloneDeep(AuthModule.user);
      const orgDetails = cloneDeep(AuthModule.orgDetails);

      const addressOLG = await AuthModule.findByIdAddress(
        orgDetails.addrDetails.registerAddressInfo
      );

      this.organization = deepmerge(this.organization, {
        name: orgDetails.fullName || null,
        ogrn: orgDetails.ogrn || null,
        inn: orgDetails.inn || null,
        email: (orgDetails.cttsDetails && orgDetails.cttsDetails.email) || null,
        address: addressOLG,

        trustee: deepmerge(this.organization.trustee, {
          lastName: userDetails.lastName || null,
          firstName: userDetails.firstName || null,
          patronymic: userDetails.middleName || null,
          snils: userDetails.snils || null,
          inn: userDetails.inn || null,
          email:
            (userDetails.contactInfo && userDetails.contactInfo.email) || null,
          phone:
            (userDetails.contactInfo && userDetails.contactInfo.mobilePhone) ||
            null,
        }),
      });
    },

    async prefillEsiaForeignOrganization() {
      const userDetails = cloneDeep(AuthModule.user);
      const orgDetails = cloneDeep(AuthModule.orgDetails);

      const addressOLG = await AuthModule.findByIdAddress(
        orgDetails.addrDetails.registerAddressInfo
      );
      const addressOPS = await AuthModule.findByIdAddress(
        orgDetails.addrDetails.currentAddressInfo
      );

      this.foreignOrganization = deepmerge(this.foreignOrganization, {
        name: orgDetails.fullName || null,
        inn: orgDetails.inn || null,
        address: addressOLG || addressOPS,

        trustee: deepmerge(this.foreignOrganization.trustee, {
          lastName: userDetails.lastName || null,
          firstName: userDetails.firstName || null,
          patronymic: userDetails.middleName || null,
          snils: userDetails.snils || null,
          inn: userDetails.inn || null,
          email:
            (userDetails.contactInfo && userDetails.contactInfo.email) || null,
          phone:
            (userDetails.contactInfo && userDetails.contactInfo.mobilePhone) ||
            null,
        }),
      });
    },

    assemblyModel() {
      const applicant = {};

      applicant[this.role] = cloneDeep(this[this.role]);
      delete applicant[this.role].trustee;

      if (this.role !== Role.PERSON) {
        applicant.trustee = cloneDeep(this[this.role]).trustee;

        if (!this[this.role].trustee.documents.statusDocument.name) {
          applicant.trustee.documents.statusDocument = null;
        }
      }

      return applicant;
    },

    touchFields() {
      this.invalidFields = [];

      this.$v[this.role].$touch();
      checkInvalid(this.$v[this.role], this.invalidFields);
    },

    btnSaveClickHandler(direction) {
      leaveStep({
        stepThis: this,
        direction,
        step: CorrectTranslationStep.APPLICANT,
      });
    },
  },
};
</script>
